import { useContext, useEffect, useMemo, useState } from "react";

import {
  Loader,
  MenuOptionSpec,
  NotificationType,
  Toolbar,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";
import { LibraryTabs } from "./components/library.tabs";

import { SessionContext } from "@src/context";
import { services } from "@src/dependencies";
import {
  useActivityStatusPolling,
  useIndependentTabLoading,
  useNotification,
} from "@src/hooks";

import { ManageActivityModal } from "../modals/manage-activity.modal";

import { ActivityUploadStatuses } from "../constants/activity-upload-status.model";
import "./index.scss";

export const LibraryList = ({ responsiveStyles }) => {
  const { programsService, libraryService } = services;

  const { activeTab, setActiveTab } = useContext(SessionContext);
  const { addNotification, removeNotification } = useNotification();

  const {
    tabStates,
    setTabLoading,
    setTabError,
    setTabData,
    retryTab,
    getTabState,
  } = useIndependentTabLoading({
    activities: { loading: false, error: undefined, data: [] },
  });

  const [loading, setLoading] = useState();
  const [openManageActivityModal, setOpenManageActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState();

  const showNotification = (type, message, icon) => {
    return addNotification({ type, message, icon });
  };

  const fetchActivityData = async () => {
    try {
      setTabLoading("activities", true);
      const activitiesData = await programsService.getAllActivities(true);
      setTabData("activities", activitiesData);
    } catch (error) {
      console.error("Failed to fetch activities:", error);
      setTabError("activities", error);
    }
  };

  const uploadFileToS3 = async (
    file,
    presignedUrlData,
    contentId,
    isNewFile
  ) => {
    const uploadingNotificationId = showNotification(
      NotificationType.info,
      "Uploading zip file..."
    );
    const id =
      presignedUrlData.contentId || (isNewFile ? contentId : undefined);

    try {
      const response = await libraryService.uploadFileToS3(
        file,
        presignedUrlData
      );

      removeNotification(uploadingNotificationId);

      if (response) {
        showNotification(
          NotificationType.success,
          "Zip file uploaded successfully."
        );
        await libraryService.updateActivityInLibrary(id, {
          status: ActivityUploadStatuses.UPLOADED_ZIP.name,
        });
      } else {
        throw new Error(response);
      }
    } catch (error) {
      removeNotification(uploadingNotificationId);
      const errorMessage = "Failed to upload zip file. Please try again.";
      await libraryService.updateActivityInLibrary(id, {
        status: ActivityUploadStatuses.FAILED_ZIP_UPLOAD.name,
        error: error?.data?.detail || error?.message || errorMessage,
      });
      showNotification(NotificationType.error, errorMessage);
    }
  };

  useEffect(() => {
    fetchActivityData();
  }, []);

  const handleRetryTab = (tabName) => {
    switch (tabName) {
      case "activities": {
        retryTab("activities", fetchActivityData);
        break;
      }
      default:
        console.warn(`Unknown tab: ${tabName}`);
    }
  };

  const onOpenEditActivityModal = (_, data) => {
    setSelectedActivity(data);
    setOpenManageActivityModal(true);
  };

  const actionMenuOptions = {
    activities: {
      edit: new MenuOptionSpec(
        "Edit Activity",
        ["fa-regular", "fa-edit"],
        onOpenEditActivityModal
      ),
    },
  };

  const currentActivitiesData = getTabState("activities")?.data || [];
  const { activityStatuses } = useActivityStatusPolling(currentActivitiesData);

  const activitiesData = useMemo(() => {
    const dataToUse =
      activityStatuses.length > 0 ? activityStatuses : currentActivitiesData;
    return {
      activitiesData: dataToUse,
    };
  }, [activityStatuses, currentActivitiesData]);

  const toolbar = (
    <Toolbar>
      <Button
        icon={["fa-regular", "fa-plus"]}
        primary
        title={"Add Library Item"}
        onClick={() => setOpenManageActivityModal(true)}
      ></Button>
    </Toolbar>
  );

  const pageHeader = <PageHeader title={"Library"}>{toolbar}</PageHeader>;

  const onActivitySaved = async (activity, presignedUrl, contentId) => {
    if (activity.file && presignedUrl.url) {
      await uploadFileToS3(
        activity.file,
        presignedUrl,
        contentId,
        activity.newFile
      );
    }
    await fetchActivityData();
  };

  return (
    <PageLayout
      className="activities-list-page"
      responsiveStyles={responsiveStyles}
      header={pageHeader}
    >
      {loading && <Loader overlay></Loader>}
      <LibraryTabs
        data={activitiesData}
        responsiveStyles={responsiveStyles}
        actionMenuOptions={actionMenuOptions}
        tabStates={tabStates}
        onRetryTab={handleRetryTab}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      {openManageActivityModal && (
        <ManageActivityModal
          show={openManageActivityModal}
          setShow={() => {
            setOpenManageActivityModal(false);
            setSelectedActivity();
          }}
          onActivitySaved={onActivitySaved}
          activity={selectedActivity}
          mode={selectedActivity ? "edit" : "add"}
        />
      )}
    </PageLayout>
  );
};
